const { getElement, getElements, focusableSelectors, isVisible } =
  window.BAO.Utils
export class Drawer extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.drawers = getElement('site-drawers')
    if (!this.drawers && this.drawers.drawers) {
      throw new Error(
        `${this.nodeName.toLowerCase()} needs a <site-drawers> element to work`,
      )
    }
    this.drawers.drawers.push(this)

    this._isOpen = false
  }

  connectedCallback () {
    super.connectedCallback()

    if (this.drawerShouldBeMoved) {
      window.setTimeout(() => {
        const moveToElement = this.moveToElement

        this.removeAttribute('data-module-drawers-move-me')

        moveToElement.appendChild(this)
      }, parseInt(this.getAttribute('data-module-drawers-move-delay'), 10) || 0)
    }
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this,
      'transitionend',
      this.handleTransitionEnd.bind(this),
    )

    this.listeners.add(document, 'keydown', this.handleKeyDown.bind(this))
  }

  handleTransitionEnd (event) {
    if (!event.target.matches(this.localName)) return
    if (event.propertyName !== 'visibility') return
    this.isVisible = getComputedStyle(this)?.visibility === 'visible'
  }

  handleKeyDown (event) {
    const KEYCODE_TAB = 9

    if (
      (event.code?.toUpperCase() === 'TAB' || event.keyCode === KEYCODE_TAB) &&
      this.isVisible
    ) {
      if (event.shiftKey) {
        if (document.activeElement === this.firstFocusableEl) {
          this.lastFocusableEl?.focus()
          event.preventDefault()
        }
      } else {
        if (document.activeElement === this.lastFocusableEl) {
          this.firstFocusableEl?.focus()
          event.preventDefault()
        }
      }
    }
  }

  get key () {
    // key is stripped by Tagalys
    return this.getAttribute('key') || this.getAttribute('data-key')
  }

  get elements () {
    return {
      trigger: getElement(`[data-drawers-trigger=${this.key}]`),
      close: getElement(`[data-module-drawers-close=${this.key}]`),
    }
  }

  get isVisible () {
    return this._isVisible
  }

  set isVisible (val) {
    this._isVisible = val

    if (val) {
      /* Move focus to the first focusable element within the drawer */
      this.firstFocusableEl?.focus()
    } else if (this.drawers.activeDrawerKey === '') {
      /* Move focus back to the element which opened the drawer */
      const visibleTriggerEl = this.drawers?.activeTriggers
        ?.reverse()
        ?.find((triggerEl) => isVisible(triggerEl))
      visibleTriggerEl?.focus()
    }
  }

  get isOpen () {
    return this._isOpen
  }

  set isOpen (val) {
    this._isOpen = val

    this.classList.toggle(this.config.classes.activeDrawerClass, val)
  }

  get drawerShouldBeMoved () {
    return this.hasAttribute('data-module-drawers-move-me')
  }

  get moveToElement () {
    switch (this.getAttribute('data-module-drawers-move-me')) {
      case 'root':
        return getElement('[data-site-drawers-el="drawer"]')
      default:
        return getElement(this.getAttribute('data-module-drawers-move-me'))
    }
  }

  get config () {
    return {
      classes: {
        activeDrawerClass: 'drw-Drawer-active',
      },
    }
  }

  get focusableEls () {
    return getElements(focusableSelectors, {
      context: this,
    })
  }

  get firstFocusableEl () {
    return this.focusableEls?.[0]
  }

  get lastFocusableEl () {
    return this.focusableEls?.[this.focusableEls.length - 1]
  }
}

if (!customElements.get('site-drawer')) {
  customElements.define('site-drawer', Drawer)
}